import React, { useState, useEffect } from "react";
import API_BASE_URL from 'config/api';
import {
  Grid,
  Divider,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  AppBar,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { ConfigCard } from "styles";
import MDTypography from "components/MDTypography";
import { BlueButton } from "styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BannerPreview from "components/BannerPreview";
import LivePreview from "components/LivePreview";

// Reusable + button that inserts a token at the cursor of a target input/textarea
const InsertAtCursorButton = ({
  getTarget,
  getMenuLabel,
  getInsertion,
  onUpdate,
  sx,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleInsert = () => {
    const target = getTarget && getTarget();
    if (!target) return closeMenu();

    const insertion = (getInsertion && getInsertion()) || "";
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;
    const value = target.value ?? "";
    const newValue = value.slice(0, start) + insertion + value.slice(end);

    if (onUpdate) onUpdate(newValue);

    setTimeout(() => {
      try {
        target.focus();
        const pos = start + insertion.length;
        target.setSelectionRange(pos, pos);
      } catch (_) {}
    }, 0);

    closeMenu();
  };

  return (
    <>
      <IconButton
        aria-label="Metne ekle"
        onClick={openMenu}
        size="small"
        sx={{
          position: "absolute",
          top: -10,
          right: 8,
          zIndex: 1,
          color: "#5e5e5eff !important",
          backgroundColor: "#ffffffff !important",
          margin: 0,
          padding: 0,
          "& .MuiSvgIcon-root": { color: "#5e5e5eff !important" },
          "&:hover": { backgroundColor: "transparent" },
          ...(sx || {}),
        }}
      >
        <AddCircleOutlineIcon fontSize="small" color="inherit" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <MenuItem onClick={handleInsert}>
          {(getMenuLabel && getMenuLabel()) || ""}
        </MenuItem>
      </Menu>
    </>
  );
};

// SABİT TÜRKÇE BAŞLIKLAR
const CATEGORY_LABELS_TR = {
  necessary: "Zorunlu Çerezler",
  functionality: "Fonksiyonel Çerezler",
  analytics: "Analiz Çerezleri",
  marketing: "Pazarlama Çerezleri",
};

const BannerDialog = () => {
  const [bannerDescInputRef, setBannerDescInputRef] = useState(null);
  const [prefsDescInputRef, setPrefsDescInputRef] = useState(null);
  const [moreInfoInputRef, setMoreInfoInputRef] = useState(null);
  const [privacyIntroInputRef, setPrivacyIntroInputRef] = useState(null);

  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [bannerSettings, setBannerSettings] = useState({});
  const [userMeData, setUserMeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewTabValue, setPreviewTabValue] = useState(0);
  const [settingsTabValue, setSettingsTabValue] = useState(0);
  const [activeLang, setActiveLang] = useState("tr");
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functionality: false,
    analytics: false,
    marketing: false,
  });
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentDomainSettings, setCurrentDomainSettings] = useState(null);
  const [currentLanguageSettings, setCurrentLanguageSettings] = useState(null);
  const [cookies, setCookies] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    const loadUserData = async () => {
      const cachedUserData = sessionStorage.getItem("userMeData");
      if (cachedUserData) {
        setUserMeData(JSON.parse(cachedUserData));
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/me`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        });
        const data = await response.json();
        sessionStorage.setItem("userMeData", JSON.stringify(data));
        setUserMeData(data);
      } catch (error) {
        console.error("Error loading user data:", error);
        showSnackbar("Kullanıcı verileri yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  useEffect(() => {
    const loadBannerSettings = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const cachedSettings = sessionStorage.getItem("bannerSettings");

        if (cachedSettings) {
          const parsedSettings = JSON.parse(cachedSettings);
          setBannerSettings(parsedSettings);
          setDomains(Object.keys(parsedSettings));

          if (Object.keys(parsedSettings).length > 0) {
            const firstDomain = Object.keys(parsedSettings)[0];
            setSelectedDomain(firstDomain);
            setCurrentDomainSettings(parsedSettings[firstDomain]);

            if (parsedSettings[firstDomain].bannerSettings[activeLang]) {
              setCurrentLanguageSettings(
                parsedSettings[firstDomain].bannerSettings[activeLang]
              );
            } else {
              const availableLangs = Object.keys(
                parsedSettings[firstDomain].bannerSettings
              );
              if (availableLangs.length > 0) {
                setActiveLang(availableLangs[0]);
                setCurrentLanguageSettings(
                  parsedSettings[firstDomain].bannerSettings[availableLangs[0]]
                );
              }
            }
          }
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/banner-settings/by-tenant?tenantId=${userMeData.user.clients[0].clientId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
          }
        );
        const data = await response.json();
        sessionStorage.setItem("bannerSettings", JSON.stringify(data));
        setBannerSettings(data);
        setDomains(Object.keys(data));

        if (Object.keys(data).length > 0) {
          const firstDomain = Object.keys(data)[0];
          setSelectedDomain(firstDomain);
          setCurrentDomainSettings(data[firstDomain]);

          if (data[firstDomain].bannerSettings[activeLang]) {
            setCurrentLanguageSettings(
              data[firstDomain].bannerSettings[activeLang]
            );
          } else {
            const availableLangs = Object.keys(
              data[firstDomain].bannerSettings
            );
            if (availableLangs.length > 0) {
              setActiveLang(availableLangs[0]);
              setCurrentLanguageSettings(
                data[firstDomain].bannerSettings[availableLangs[0]]
              );
            }
          }
        }
      } catch (error) {
        console.error("Error loading banner settings:", error);
        showSnackbar("Banner ayarları yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadBannerSettings();
  }, [userMeData]);

  useEffect(() => {
    const loadCookieData = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const cachedSettings = sessionStorage.getItem("userCookies");

        if (cachedSettings && Object.keys(cachedSettings).length > 0) {
          const parsedSettings = JSON.parse(cachedSettings);
          setCookies(parsedSettings);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/cookie-definition/all?tenantId=${userMeData.user.clients[0].clientId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
          }
        );
        const data = await response.json();

        if (Object.keys(data).length > 0) {
          sessionStorage.setItem("userCookies", JSON.stringify(data));
          setCookies(data);
        }
      } catch (error) {
        console.error("Error loading cookies:", error);
        showSnackbar("Çerezler yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadCookieData();
  }, [userMeData]);

  useEffect(() => {
    if (selectedDomain && bannerSettings[selectedDomain]) {
      const domainSettings = bannerSettings[selectedDomain];
      setCurrentDomainSettings(domainSettings);

      const defaultLang = domainSettings.defaultLanguage || activeLang;

      if (domainSettings.bannerSettings[defaultLang]) {
        setActiveLang(defaultLang);
        setCurrentLanguageSettings(domainSettings.bannerSettings[defaultLang]);
      } else if (domainSettings.bannerSettings[activeLang]) {
        setCurrentLanguageSettings(domainSettings.bannerSettings[activeLang]);
      } else {
        const availableLangs = Object.keys(domainSettings.bannerSettings);
        if (availableLangs.length > 0) {
          const fallbackLang = availableLangs[0];
          setActiveLang(fallbackLang);
          setCurrentLanguageSettings(
            domainSettings.bannerSettings[fallbackLang]
          );
        }
      }
    }
  }, [selectedDomain]);

  useEffect(() => {
    if (
      currentDomainSettings &&
      currentDomainSettings.bannerSettings[activeLang]
    ) {
      setCurrentLanguageSettings(
        currentDomainSettings.bannerSettings[activeLang]
      );
    }
  }, [activeLang, currentDomainSettings]);

  const handleCategoryToggle = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDomainChange = (event) => {
    setSelectedDomain(event.target.value);
  };

  const handlePreviewTabChange = (event, newValue) => {
    setPreviewTabValue(newValue);
  };

  const handleSettingsTabChange = (event, newValue) => {
    setSettingsTabValue(newValue);
  };

  const handlePreferenceChange = (category) => (event) => {
    setCookiePreferences({
      ...cookiePreferences,
      [category]: event.target.checked,
    });
  };

  const handleLangChange = (e) => {
    setActiveLang(e.target.value);
  };

  const handleBannerTextsChange = (field, value) => {
    if (!currentDomainSettings || !currentLanguageSettings) return;

    const updatedBannerTexts = {
      ...currentLanguageSettings.bannerTexts,
      [field]: value,
    };

    const updatedLanguageSettings = {
      ...currentLanguageSettings,
      bannerTexts: updatedBannerTexts,
    };

    const updatedDomainSettings = {
      ...currentDomainSettings,
      bannerSettings: {
        ...currentDomainSettings.bannerSettings,
        [activeLang]: updatedLanguageSettings,
      },
    };

    setCurrentLanguageSettings(updatedLanguageSettings);
    setCurrentDomainSettings(updatedDomainSettings);

    setBannerSettings((prev) => ({
      ...prev,
      [selectedDomain]: updatedDomainSettings,
    }));
  };

  const handleCategoryChange = (category, field, value) => {
    if (!currentDomainSettings || !currentLanguageSettings) return;

    const updatedCategories = currentLanguageSettings.categories.map((cat) => {
      if (cat.category === category) {
        return { ...cat, [field]: value };
      }
      return cat;
    });

    const updatedLanguageSettings = {
      ...currentLanguageSettings,
      categories: updatedCategories,
    };

    const updatedDomainSettings = {
      ...currentDomainSettings,
      bannerSettings: {
        ...currentDomainSettings.bannerSettings,
        [activeLang]: updatedLanguageSettings,
      },
    };

    setCurrentLanguageSettings(updatedLanguageSettings);
    setCurrentDomainSettings(updatedDomainSettings);

    setBannerSettings((prev) => ({
      ...prev,
      [selectedDomain]: updatedDomainSettings,
    }));
  };

  const handleDialogHeadingChange = (dialog, value) => {
    if (!currentDomainSettings || !currentLanguageSettings) return;

    const updatedDialogHeadings = {
      ...currentLanguageSettings.dialogHeadings,
      [dialog]: value,
    };

    const updatedLanguageSettings = {
      ...currentLanguageSettings,
      dialogHeadings: updatedDialogHeadings,
    };

    const updatedDomainSettings = {
      ...currentDomainSettings,
      bannerSettings: {
        ...currentDomainSettings.bannerSettings,
        [activeLang]: updatedLanguageSettings,
      },
    };

    setCurrentLanguageSettings(updatedLanguageSettings);
    setCurrentDomainSettings(updatedDomainSettings);

    setBannerSettings((prev) => ({
      ...prev,
      [selectedDomain]: updatedDomainSettings,
    }));
  };

  const handleInformationalTextsChange = (dialog, value) => {
    if (!currentDomainSettings || !currentLanguageSettings) return;

    const updatedDialogHeadings = {
      ...currentLanguageSettings.informationalTexts,
      [dialog]: value,
    };

    const updatedLanguageSettings = {
      ...currentLanguageSettings,
      informationalTexts: updatedDialogHeadings,
    };

    const updatedDomainSettings = {
      ...currentDomainSettings,
      bannerSettings: {
        ...currentDomainSettings.bannerSettings,
        [activeLang]: updatedLanguageSettings,
      },
    };

    setCurrentLanguageSettings(updatedLanguageSettings);
    setCurrentDomainSettings(updatedDomainSettings);

    setBannerSettings((prev) => ({
      ...prev,
      [selectedDomain]: updatedDomainSettings,
    }));
  };

  const handlePreferenceTextsChange = (field, value) => {
    if (!currentDomainSettings || !currentLanguageSettings) return;

    const updatedBannerTexts = {
      ...currentLanguageSettings.preferenceTexts,
      [field]: value,
    };

    const updatedLanguageSettings = {
      ...currentLanguageSettings,
      preferenceTexts: updatedBannerTexts,
    };

    const updatedDomainSettings = {
      ...currentDomainSettings,
      bannerSettings: {
        ...currentDomainSettings.bannerSettings,
        [activeLang]: updatedLanguageSettings,
      },
    };

    setCurrentLanguageSettings(updatedLanguageSettings);
    setCurrentDomainSettings(updatedDomainSettings);

    setBannerSettings((prev) => ({
      ...prev,
      [selectedDomain]: updatedDomainSettings,
    }));
  };

  const handleBannerSettingsChange = (field, value) => {
    if (!currentDomainSettings || !currentLanguageSettings) return;

    const updatedBannerTexts = {
      ...currentLanguageSettings,
      [field]: value,
    };

    const updatedDomainSettings = {
      ...currentDomainSettings,
      bannerSettings: {
        ...currentDomainSettings.bannerSettings,
        [activeLang]: updatedBannerTexts,
      },
    };

    setCurrentLanguageSettings(updatedBannerTexts);
    setCurrentDomainSettings(updatedDomainSettings);

    setBannerSettings((prev) => ({
      ...prev,
      [selectedDomain]: updatedDomainSettings,
    }));
  };

  const handleAcceptAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleCustomize = () => {
    setOpenModal(!openModal);
  };

  const handleAcceptSelection = () => {
    setOpenModal(false);
  };

  const handleSave = async () => {
    if (!currentDomainSettings || !selectedDomain || !userMeData) return;

    try {
      setSaving(true);

      const payload = {
        siteId: currentDomainSettings.id,
        language: activeLang,
        ...currentLanguageSettings,
      };

      const response = await fetch(
        `${API_BASE_URL}/banner-settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Güncelleme başarısız oldu");
      }

      const updatedSettings = { ...bannerSettings };
      updatedSettings[selectedDomain] = currentDomainSettings;

      sessionStorage.setItem("bannerSettings", JSON.stringify(updatedSettings));
      setBannerSettings(updatedSettings);

      showSnackbar("Dialog ayarları başarıyla kaydedildi", "success");
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      showSnackbar("Dialog ayarları kaydedilirken hata oluştu", "error");
    } finally {
      setSaving(false);
    }
  };

  if (
    selectedDomain &&
    bannerSettings &&
    bannerSettings[selectedDomain] &&
    (!bannerSettings[selectedDomain].bannerSettings ||
      Object.keys(bannerSettings[selectedDomain].bannerSettings).length === 0)
  ) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          flexDirection="column"
        >
          <Typography variant="h6" mb={2}>
            Bir sitenizde dil ayarlarını tamamlamadınız.
          </Typography>
          <BlueButton
            onClick={() => (window.location.href = "/configuration/language")}
          >
            Dil Ayarlarına Git
          </BlueButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!currentDomainSettings || !currentLanguageSettings) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress size={50} />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const themeSettings = currentDomainSettings.themeSettings;
  const bannerType = themeSettings.type;
  const availableLangs = Object.keys(currentDomainSettings.bannerSettings);

  return (
    <DashboardLayout>
      <MDBox mt={2} my={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AppBar position="static">
              {(() => {
                const tabLabels =
                  bannerType === "dialog"
                    ? [
                        "Giriş İçeriği",
                        "Butonlar",
                        "Çerez Kategorileri",
                        "Dialog Başlıkları",
                      ]
                    : [
                        "Banner İçeriği",
                        "Butonlar",
                        "Çerez Kategorileri",
                        "Tercihler Detay Metinleri",
                      ];
                return (
                  <Tabs
                    value={settingsTabValue}
                    onChange={handleSettingsTabChange}
                    sx={{ backgroundColor: "#ddd" }}
                  >
                    {tabLabels.map((lbl) => (
                      <Tab key={lbl} label={lbl} />
                    ))}
                  </Tabs>
                );
              })()}
            </AppBar>
          </Grid>

          <Grid item xs={12} md={4}>
            <ConfigCard>
              <MDBox p={3}>
                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Domain
                </MDTypography>
                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                      background: "#fafbfc",
                      fontSize: 16,
                      padding: "4px 12px",
                      "& .MuiSelect-icon": {
                        color: "#000",
                        right: "12px",
                      },
                    },
                  }}
                >
                  <Select
                    value={selectedDomain}
                    onChange={handleDomainChange}
                    IconComponent={(props) => (
                      <KeyboardArrowDownIcon {...props} fontSize="medium" />
                    )}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                      borderRadius: "12px",
                      ".MuiSelect-select": {
                        padding: "14px 32px 14px 12px",
                      },
                      "& .MuiSelect-icon": {
                        display: "block !important",
                      },
                    }}
                  >
                    {domains.map((domain) => (
                      <MenuItem key={domain} value={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Dil
                </MDTypography>
                <FormControl
                  fullWidth
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                      background: "#fafbfc",
                      fontSize: 16,
                      padding: "4px 12px",
                      "& .MuiSelect-icon": {
                        color: "#000",
                        right: "12px",
                      },
                    },
                  }}
                >
                  <Select
                    value={activeLang}
                    onChange={handleLangChange}
                    IconComponent={(props) => (
                      <KeyboardArrowDownIcon {...props} fontSize="medium" />
                    )}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                      borderRadius: "12px",
                      ".MuiSelect-select": {
                        padding: "14px 32px 14px 12px",
                      },
                      "& .MuiSelect-icon": {
                        display: "block !important",
                      },
                    }}
                  >
                    {availableLangs.includes("tr") && (
                      <MenuItem value="tr">Türkçe</MenuItem>
                    )}
                    {availableLangs.includes("en") && (
                      <MenuItem value="en">İngilizce</MenuItem>
                    )}
                  </Select>
                </FormControl>

                {settingsTabValue === 0 && (
                  <>
                    <Typography variant="h6" mb={2}>
                      {themeSettings.type === "banner"
                        ? "Banner İçeriği"
                        : "Genel içerik"}
                    </Typography>
                    <TextField
                      fullWidth
                      label={
                        themeSettings.type === "banner"
                          ? "Banner Başlık"
                          : "Genel içerik Başlık"
                      }
                      value={
                        currentLanguageSettings.bannerTexts?.bannerTitle || ""
                      }
                      onChange={(e) =>
                        handleBannerTextsChange("bannerTitle", e.target.value)
                      }
                      sx={{ mb: 3 }}
                    />
                    <Box sx={{ position: "relative", mb: 3 }}>
                      <InsertAtCursorButton
                        getTarget={() => bannerDescInputRef}
                        getMenuLabel={() =>
                          (
                            currentLanguageSettings.bannerTexts
                              ?.cookiePolicyLinkText || ""
                          ).replace(/\[\[|\]\]/g, "")
                        }
                        getInsertion={() => {
                          const raw =
                            currentLanguageSettings.bannerTexts
                              ?.cookiePolicyLinkText || "";
                          return `[[${raw.replace(/\[\[|\]\]/g, "")}]]`;
                        }}
                        onUpdate={(newValue) =>
                          handleBannerTextsChange("bannerDescription", newValue)
                        }
                      />
                      <TextField
                        fullWidth
                        label={
                          themeSettings.type === "banner"
                            ? "Banner Açıklama"
                            : "Genel içerik Açıklama"
                        }
                        multiline
                        rows={6}
                        value={
                          currentLanguageSettings.bannerTexts
                            ?.bannerDescription || ""
                        }
                        onChange={(e) => {
                          const text = e.target.value;
                          handleBannerTextsChange("bannerDescription", text);
                        }}
                        inputRef={(el) => setBannerDescInputRef(el)}
                        helperText="Link oluşturduğunuz metni '+' butonuna basarak dinamik olarak yerleştirebilirsiniz"
                      />
                    </Box>
                    <Typography variant="h6" mb={2}>
                      Çerez Güvenlik Politikası
                    </Typography>
                    <TextField
                      fullWidth
                      label="Çerez Güvenlik Politikası Link Metni"
                      multiline
                      sx={{ mb: 3 }}
                      value={(
                        currentLanguageSettings.bannerTexts
                          ?.cookiePolicyLinkText || ""
                      ).replace(/\[\[|\]\]/g, "")}
                      onChange={(e) => {
                        const clean = (e.target.value || "").replace(
                          /\[\[|\]\]/g,
                          ""
                        );
                        handleBannerTextsChange(
                          "cookiePolicyLinkText",
                          `[[${clean}]]`
                        );
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Çerez Güvenlik Politikası Linki"
                      multiline
                      value={currentLanguageSettings.cookiePolicyUrl || ""}
                      onChange={(e) =>
                        handleBannerSettingsChange(
                          "cookiePolicyUrl",
                          e.target.value
                        )
                      }
                      sx={{ mb: 3 }}
                    />
                    <Typography variant="h6" mb={2}>
                      Logo Ayarları
                    </Typography>
                    <TextField
                      fullWidth
                      label="Logo Linki"
                      multiline
                      value={currentLanguageSettings.logoUrl || ""}
                      onChange={(e) =>
                        handleBannerSettingsChange("logoUrl", e.target.value)
                      }
                    />
                  </>
                )}

                {settingsTabValue === 1 && (
                  <>
                    <Typography variant="h6" mb={2}>
                      Buton Metinleri
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Reddet butonu"
                          value={
                            currentLanguageSettings.bannerTexts?.rejectText ||
                            ""
                          }
                          onChange={(e) =>
                            handleBannerTextsChange(
                              "rejectText",
                              e.target.value
                            )
                          }
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Tümünü kabul et butonu"
                          value={
                            currentLanguageSettings.bannerTexts?.acceptText ||
                            ""
                          }
                          onChange={(e) =>
                            handleBannerTextsChange(
                              "acceptText",
                              e.target.value
                            )
                          }
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Özelleştir butonu"
                          value={
                            currentLanguageSettings.bannerTexts
                              ?.customizeText || ""
                          }
                          onChange={(e) =>
                            handleBannerTextsChange(
                              "customizeText",
                              e.target.value
                            )
                          }
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Tercihleri kaydet butonu"
                          value={
                            currentLanguageSettings.preferenceTexts
                              ?.savePreferencesText || ""
                          }
                          onChange={(e) =>
                            handlePreferenceTextsChange(
                              "savePreferencesText",
                              e.target.value
                            )
                          }
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {settingsTabValue === 2 && (
                  <>
                    <Typography variant="h6" mb={2}>
                      Çerez Kategorileri
                    </Typography>
                    <TextField
                      fullWidth
                      label="Genel çerez başlığı"
                      multiline
                      value={
                        currentLanguageSettings.preferenceTexts
                          ?.preferencesTitle || ""
                      }
                      onChange={(e) =>
                        handlePreferenceTextsChange(
                          "preferencesTitle",
                          e.target.value
                        )
                      }
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ position: "relative", mb: 3 }}>
                      <InsertAtCursorButton
                        getTarget={() => prefsDescInputRef}
                        getMenuLabel={() =>
                          (
                            currentLanguageSettings.bannerTexts
                              ?.cookiePolicyLinkText || ""
                          ).replace(/\[\[|\]\]/g, "")
                        }
                        getInsertion={() => {
                          const raw =
                            currentLanguageSettings.bannerTexts
                              ?.cookiePolicyLinkText || "";
                          return `[[${raw.replace(/\[\[|\]\]/g, "")}]]`;
                        }}
                        onUpdate={(newValue) =>
                          handlePreferenceTextsChange(
                            "preferencesDescription",
                            newValue
                          )
                        }
                      />
                      <TextField
                        fullWidth
                        label="Genel çerez açıklaması"
                        multiline
                        rows={6}
                        value={
                          currentLanguageSettings.preferenceTexts
                            ?.preferencesDescription || ""
                        }
                        onChange={(e) =>
                          handlePreferenceTextsChange(
                            "preferencesDescription",
                            e.target.value
                          )
                        }
                        inputRef={(el) => setPrefsDescInputRef(el)}
                        helperText="Link oluşturduğunuz metni '+' butonuna basarak dinamik olarak yerleştirebilirsiniz"
                      />
                    </Box>
                    {currentLanguageSettings.categories?.map((category) => (
                      <Box key={category.category} mb={3}>
                        <Typography variant="subtitle1" mb={1}>
                          {CATEGORY_LABELS_TR[category.category] ||
                            category.category}
                        </Typography>
                        <TextField
                          fullWidth
                          label="Çerez başlığı"
                          value={category.name || ""}
                          onChange={(e) =>
                            handleCategoryChange(
                              category.category,
                              "name",
                              e.target.value
                            )
                          }
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Çerez açıklaması"
                          multiline
                          rows={3}
                          value={category.description || ""}
                          onChange={(e) =>
                            handleCategoryChange(
                              category.category,
                              "description",
                              e.target.value
                            )
                          }
                        />
                      </Box>
                    ))}
                  </>
                )}

                {settingsTabValue === 3 &&
                  (bannerType === "dialog" ? (
                    <>
                      <Typography variant="h6" mb={2}>
                        Dialog Başlıkları ve Detay Metinleri
                      </Typography>
                      <TextField
                        fullWidth
                        label="Genel dialog başlığı"
                        multiline
                        value={
                          currentLanguageSettings.dialogHeadings
                            ?.dialogGeneralHeading || ""
                        }
                        onChange={(e) =>
                          handleDialogHeadingChange(
                            "dialogGeneralHeading",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Çerezler dialog başlığı"
                        multiline
                        value={
                          currentLanguageSettings.dialogHeadings
                            ?.dialogCookiesHeading || ""
                        }
                        onChange={(e) =>
                          handleDialogHeadingChange(
                            "dialogCookiesHeading",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Detaylar dialog başlığı"
                        multiline
                        value={
                          currentLanguageSettings.dialogHeadings
                            ?.dialogDetailsHeading || ""
                        }
                        onChange={(e) =>
                          handleDialogHeadingChange(
                            "dialogDetailsHeading",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ position: "relative", mb: 3 }}>
                        <InsertAtCursorButton
                          getTarget={() => moreInfoInputRef}
                          getMenuLabel={() =>
                            (
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || ""
                            ).replace(/\[\[|\]\]/g, "")
                          }
                          getInsertion={() => {
                            const raw =
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || "";
                            return `[[${raw.replace(/\[\[|\]\]/g, "")}]]`;
                          }}
                          onUpdate={(newValue) =>
                            handleInformationalTextsChange(
                              "moreInfoText",
                              newValue
                            )
                          }
                        />
                        <TextField
                          fullWidth
                          label="Detaylar Ek içerik"
                          multiline
                          rows={4}
                          value={
                            currentLanguageSettings.informationalTexts
                              ?.moreInfoText || ""
                          }
                          onChange={(e) =>
                            handleInformationalTextsChange(
                              "moreInfoText",
                              e.target.value
                            )
                          }
                          inputRef={(el) => setMoreInfoInputRef(el)}
                          helperText="Link oluşturduğunuz metni '+' butonuna basarak dinamik olarak yerleştirebilirsiniz"
                        />
                      </Box>
                      <Box sx={{ position: "relative", mb: 3 }}>
                        <InsertAtCursorButton
                          getTarget={() => privacyIntroInputRef}
                          getMenuLabel={() =>
                            (
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || ""
                            ).replace(/\[\[|\]\]/g, "")
                          }
                          getInsertion={() => {
                            const raw =
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || "";
                            return `[[${raw.replace(/\[\[|\]\]/g, "")}]]`;
                          }}
                          onUpdate={(newValue) =>
                            handleInformationalTextsChange(
                              "privacyIntroText",
                              newValue
                            )
                          }
                        />
                        <TextField
                          fullWidth
                          label="Detaylar İçerik 2"
                          multiline
                          rows={8}
                          value={
                            currentLanguageSettings.informationalTexts
                              ?.privacyIntroText || ""
                          }
                          onChange={(e) =>
                            handleInformationalTextsChange(
                              "privacyIntroText",
                              e.target.value
                            )
                          }
                          helperText="Link oluşturduğunuz metni '+' butonuna basarak dinamik olarak yerleştirebilirsiniz"
                          sx={{ mb: 0 }}
                          inputRef={(el) => setPrivacyIntroInputRef(el)}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" mb={2}>
                        Tercihler Detay Metinleri
                      </Typography>
                      <TextField
                        fullWidth
                        label="Detaylar başlığı"
                        multiline
                        value={
                          currentLanguageSettings.dialogHeadings
                            ?.dialogDetailsHeading || ""
                        }
                        onChange={(e) =>
                          handleDialogHeadingChange(
                            "dialogDetailsHeading",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ position: "relative", mb: 3 }}>
                        <InsertAtCursorButton
                          getTarget={() => moreInfoInputRef}
                          getMenuLabel={() =>
                            (
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || ""
                            ).replace(/\[\[|\]\]/g, "")
                          }
                          getInsertion={() => {
                            const raw =
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || "";
                            return `[[${raw.replace(/\[\[|\]\]/g, "")}]]`;
                          }}
                          onUpdate={(newValue) =>
                            handleInformationalTextsChange(
                              "moreInfoText",
                              newValue
                            )
                          }
                        />
                        <TextField
                          fullWidth
                          label="Tercihler Ek içerik"
                          multiline
                          rows={4}
                          value={
                            currentLanguageSettings.informationalTexts
                              ?.moreInfoText || ""
                          }
                          onChange={(e) =>
                            handleInformationalTextsChange(
                              "moreInfoText",
                              e.target.value
                            )
                          }
                          helperText="Link oluşturduğunuz metni '+' butonuna basarak dinamik olarak yerleştirebilirsiniz"
                          sx={{ mb: 0 }}
                          inputRef={(el) => setMoreInfoInputRef(el)}
                        />
                      </Box>
                      <Box sx={{ position: "relative", mb: 3 }}>
                        <InsertAtCursorButton
                          getTarget={() => privacyIntroInputRef}
                          getMenuLabel={() =>
                            (
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || ""
                            ).replace(/\[\[|\]\]/g, "")
                          }
                          getInsertion={() => {
                            const raw =
                              currentLanguageSettings.bannerTexts
                                ?.cookiePolicyLinkText || "";
                            return `[[${raw.replace(/\[\[|\]\]/g, "")}]]`;
                          }}
                          onUpdate={(newValue) =>
                            handleInformationalTextsChange(
                              "privacyIntroText",
                              newValue
                            )
                          }
                        />
                        <TextField
                          fullWidth
                          label="Tercihler Ek İçerik 2"
                          multiline
                          rows={8}
                          value={
                            currentLanguageSettings.informationalTexts
                              ?.privacyIntroText || ""
                          }
                          onChange={(e) =>
                            handleInformationalTextsChange(
                              "privacyIntroText",
                              e.target.value
                            )
                          }
                          helperText="Link oluşturduğunuz metni '+' butonuna basarak dinamik olarak yerleştirebilirsiniz"
                          sx={{ mb: 0 }}
                          inputRef={(el) => setPrivacyIntroInputRef(el)}
                        />
                      </Box>
                    </>
                  ))}

                <Divider sx={{ my: 3 }} />
                <BlueButton
                  fullWidth
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </BlueButton>
              </MDBox>
            </ConfigCard>
          </Grid>

          <Grid item xs={12} md={8}>
            <LivePreview
              cookieContentState={{
                banner: (
                  <BannerPreview
                    themeSettings={themeSettings}
                    currentLanguageSettings={currentLanguageSettings}
                    currentDomainSettings={currentDomainSettings}
                    cookies={cookies}
                    activeLang={activeLang}
                    previewTabValue={previewTabValue}
                    openModal={openModal}
                    cookiePreferences={cookiePreferences}
                    expandedCategories={expandedCategories}
                    availableLangs={availableLangs}
                    onLangChange={handleLangChange}
                    onPreviewTabChange={handlePreviewTabChange}
                    onCustomize={handleCustomize}
                    onAcceptAll={handleAcceptAll}
                    onRejectAll={handleRejectAll}
                    onAcceptSelection={handleAcceptSelection}
                    onPreferenceChange={handlePreferenceChange}
                    onCategoryToggle={handleCategoryToggle}
                    onSnackbar={showSnackbar}
                  />
                ),
              }}
              selectedDomain={selectedDomain}
              defaultLanguage={currentDomainSettings.defaultLanguage}
            />
          </Grid>
        </Grid>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
};

export default BannerDialog;